// Cycle Report helper.
//
// Pure data layer for the Purchase Cycle and Sale Cycle reports.
// Builds an in-memory graph of transactions linked by parentId/childIds plus
// invoice→payment edges resolved from each payment's invoiceAllocations[].
//
// No writes. Only reads from the existing transactions collection — so the
// reports can never disturb posted data.
//
// Output shape:
//   {
//     nodes: [{ id, tranTypeCode, lane, docNumber, ...selectedFields }],
//     edges: [{ from: nodeId, to: nodeId, kind: 'structural' | 'payment' | 'return' }],
//     chains: [{ rootId, nodeIds: [...] }],
//     stats: { /* aggregates per lane for the KPI strip */ }
//   }

import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../config/firebase';

// Lane definitions. Same engine, two configs.
export const PURCHASE_LANES = [
  { id: 'order',    label: 'Order',    types: ['REQ', 'PO'] },
  { id: 'receipt',  label: 'Receipt',  types: ['GRN'] },
  { id: 'invoice',  label: 'Invoice',  types: ['PI', 'NPI', 'IPI'] },
  { id: 'return',   label: 'Return',   types: ['PR', 'NPR'] },
  { id: 'payment',  label: 'Payment',  types: ['CP', 'BP'] }
];

export const SALE_LANES = [
  { id: 'quote',    label: 'Quote',    types: ['QT'] },
  { id: 'order',    label: 'Order',    types: ['SO'] },
  { id: 'delivery', label: 'Delivery', types: ['DC'] },
  { id: 'invoice',  label: 'Invoice',  types: ['SI', 'NSI'] },
  { id: 'return',   label: 'Return',   types: ['SR', 'NSR'] },
  { id: 'receipt',  label: 'Receipt',  types: ['CR', 'BR'] }
];

// Reverse map: tranTypeCode → lane id.
const buildLaneMap = (lanes) => {
  const m = {};
  lanes.forEach(lane => lane.types.forEach(code => { m[code] = lane.id; }));
  return m;
};

// Firestore `in` clauses cap at 30 values; we have at most 11 codes per cycle.
// Date range is applied via docDate string compare (YYYY-MM-DD format throughout).
const fetchTransactionsByCodes = async (companyId, codes, fromDate, toDate, branchId) => {
  if (!codes || codes.length === 0) return [];
  const txnRef = collection(db, 'companies', companyId, 'transactions');
  const conds = [where('tranTypeCode', 'in', codes)];
  if (fromDate) conds.push(where('docDate', '>=', fromDate));
  if (toDate)   conds.push(where('docDate', '<=', toDate));
  // Branch filter: when null/empty/'all' we skip the filter; null branchIds
  // (multi-branch off) and explicit branch IDs are queryable directly.
  if (branchId && branchId !== 'all') conds.push(where('branchId', '==', branchId));
  const snap = await getDocs(query(txnRef, ...conds));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Walk parentId/childIds upward and downward to ensure every chain is whole,
// even when an ancestor or descendant falls OUTSIDE the requested date range.
// Without this, a PO from 6 months ago whose PI just landed today would render
// as a stranded PI with a "missing parent" stub. We pull missing nodes by id
// in batches (Firestore `in` query, capped at 30 ids per batch).
const fetchMissingNodes = async (companyId, missingIds) => {
  if (!missingIds || missingIds.length === 0) return [];
  const txnRef = collection(db, 'companies', companyId, 'transactions');
  const all = [];
  // Batch in groups of 30 (Firestore `in` limit).
  for (let i = 0; i < missingIds.length; i += 30) {
    const batchIds = missingIds.slice(i, i + 30);
    const snap = await getDocs(query(txnRef, where(documentId(), 'in', batchIds)));
    snap.docs.forEach(d => all.push({ id: d.id, ...d.data() }));
  }
  return all;
};

// Resolve a set of user UIDs to display names (one read per uid, batched).
// Used by the audit popover. Falls back to the uid itself when a profile is
// missing (deleted user) so the UI never shows "undefined".
export const resolveUserNames = async (companyId, uids) => {
  const unique = [...new Set((uids || []).filter(Boolean))];
  if (unique.length === 0) return {};
  const usersRef = collection(db, 'companies', companyId, 'users');
  const out = {};
  for (let i = 0; i < unique.length; i += 30) {
    const batch = unique.slice(i, i + 30);
    try {
      const snap = await getDocs(query(usersRef, where(documentId(), 'in', batch)));
      snap.docs.forEach(d => {
        const data = d.data();
        out[d.id] = data.displayName || data.email || d.id;
      });
    } catch (err) {
      // Fall through — some uids will be missing from the company users
      // collection (e.g. super-admin shadow login). They render as raw uid.
      console.warn('[cycleReport] user resolve batch failed:', err?.message);
    }
  }
  unique.forEach(uid => { if (!out[uid]) out[uid] = uid; });
  return out;
};

// Compute simple KPIs across all visible nodes for the top pipeline strip.
// Numbers are intentionally additive (sum of totals across chains in view).
const computeStats = (nodes, lanesConfig) => {
  const byLane = {};
  lanesConfig.forEach(l => { byLane[l.id] = { count: 0, total: 0 }; });
  let totalPaid = 0;
  let totalInvoiced = 0;
  for (const n of nodes) {
    if (!n.lane) continue;
    const bucket = byLane[n.lane];
    if (!bucket) continue;
    bucket.count += 1;
    bucket.total += Number(n.totalAmount) || 0;
    // Track paid/invoiced separately for the Outstanding pill — only invoice
    // docs carry paidAmount in the spec; cancelled docs are skipped.
    if (['PI', 'NPI', 'IPI', 'SI', 'NSI'].includes(n.tranTypeCode) && n.status !== 'Cancelled') {
      totalInvoiced += Number(n.totalAmount) || 0;
      totalPaid += Number(n.paidAmount) || 0;
    }
  }
  return {
    byLane,
    totalInvoiced,
    totalPaid,
    outstanding: Math.max(0, totalInvoiced - totalPaid)
  };
};

// Project a transaction document down to the fields the canvas actually
// needs. Keeps memory small and prevents accidental UI coupling to schema
// changes elsewhere.
const projectNode = (txn, laneMap) => ({
  id: txn.id,
  tranTypeCode: txn.tranTypeCode,
  lane: laneMap[txn.tranTypeCode] || null,
  docNumber: txn.docNumber || '',
  docDate: txn.docDate || '',
  status: txn.status || '',
  partyType: txn.partyType || null,
  partyId: txn.partyId || null,
  partyName: txn.partyName || '',
  totalAmount: Number(txn.totalAmount) || 0,
  paidAmount: Number(txn.paidAmount) || 0,
  balanceAmount: Number(txn.balanceAmount) || 0,
  branchId: txn.branchId || null,
  branchName: txn.branchName || null,
  parentId: txn.parentId || null,
  childIds: Array.isArray(txn.childIds) ? txn.childIds : [],
  invoiceAllocations: Array.isArray(txn.invoiceAllocations) ? txn.invoiceAllocations : [],
  // Audit metadata — surfaced by the audit popover.
  createdBy: txn.createdBy || null,
  createdAt: txn.createdAt || null,
  updatedBy: txn.updatedBy || null,
  updatedAt: txn.updatedAt || null,
  postedBy: txn.postedBy || null,
  postedAt: txn.postedAt || null,
  // Lines snapshot — used by the side panel and 3-way-match badges.
  lines: Array.isArray(txn.lines) ? txn.lines : []
});

// Determine which chains are "stuck" using a terminal-stage rule: a chain is
// stuck if its newest node is older than `staleAfterDays` and the chain has
// not reached the terminal lane. Skipping middle stages never triggers a
// false flag — the rule only cares about whether the chain finished.
const computeStuck = (chains, nodes, terminalLaneId, staleAfterDays) => {
  const today = Date.now();
  const stuck = [];
  for (const chain of chains) {
    const chainNodes = chain.nodeIds.map(id => nodes.find(n => n.id === id)).filter(Boolean);
    if (chainNodes.length === 0) continue;
    const reachedTerminal = chainNodes.some(n => n.lane === terminalLaneId);
    if (reachedTerminal) continue;
    // Newest node by docDate.
    let newest = chainNodes[0];
    for (const n of chainNodes) {
      if ((n.docDate || '') > (newest.docDate || '')) newest = n;
    }
    if (!newest.docDate) continue;
    const ageDays = Math.round((today - new Date(newest.docDate).getTime()) / (1000 * 60 * 60 * 24));
    if (ageDays >= staleAfterDays) {
      stuck.push({
        chain,
        rootDocNumber: chainNodes.find(n => n.id === chain.rootId)?.docNumber || newest.docNumber,
        rootDocId: chain.rootId,
        rootTranType: chainNodes.find(n => n.id === chain.rootId)?.tranTypeCode || newest.tranTypeCode,
        bottleneckNode: newest,
        ageDays
      });
    }
  }
  // Sort oldest-first so the worst offender is on top.
  return stuck.sort((a, b) => b.ageDays - a.ageDays);
};

// ── Public entry point ─────────────────────────────────────────────────────

/**
 * Build a cycle graph for one period.
 *
 * @param {string} companyId
 * @param {object} opts
 *   - lanes: PURCHASE_LANES | SALE_LANES
 *   - fromDate: 'YYYY-MM-DD'
 *   - toDate:   'YYYY-MM-DD'
 *   - branchId: string | null | 'all'
 *   - staleAfterDays: number (default 30)
 */
export const buildCycleGraph = async (companyId, opts) => {
  const { lanes, fromDate, toDate, branchId, staleAfterDays = 30 } = opts || {};
  if (!companyId) throw new Error('buildCycleGraph: companyId is required');
  if (!Array.isArray(lanes) || lanes.length === 0) throw new Error('buildCycleGraph: lanes config required');

  const laneMap = buildLaneMap(lanes);
  const allCodes = lanes.flatMap(l => l.types);

  // 1. Fetch every doc whose code is in our cycle and whose date is in range.
  const initial = await fetchTransactionsByCodes(companyId, allCodes, fromDate, toDate, branchId);
  const byId = new Map(initial.map(t => [t.id, t]));

  // 2. Walk parentId / childIds outward to pull in any out-of-range ancestors
  //    or descendants so chains are never half-rendered.
  const referencedIds = new Set();
  const collect = (id) => { if (id && !byId.has(id)) referencedIds.add(id); };
  initial.forEach(t => {
    collect(t.parentId);
    (t.childIds || []).forEach(collect);
    (t.invoiceAllocations || []).forEach(a => collect(a.invoiceId));
  });
  // Hop outward up to a couple of levels until every referenced ancestor /
  // descendant / payment-target is in the map. Two hops handles the rare
  // REQ-grandparent case without an unbounded loop.
  for (let hop = 0; hop < 3 && referencedIds.size > 0; hop++) {
    const toFetch = [...referencedIds];
    referencedIds.clear();
    const fetched = await fetchMissingNodes(companyId, toFetch);
    fetched.forEach(t => byId.set(t.id, t));
    fetched.forEach(t => {
      collect(t.parentId);
      (t.childIds || []).forEach(collect);
      (t.invoiceAllocations || []).forEach(a => collect(a.invoiceId));
    });
  }

  // 3. Project to lightweight node shape.
  const nodes = Array.from(byId.values())
    .filter(t => laneMap[t.tranTypeCode]) // ignore foreign types just in case
    .map(t => projectNode(t, laneMap));

  // 4. Build edges. Two kinds:
  //    a) Structural: child.parentId → parent.id.
  //    b) Payment: payment.invoiceAllocations[].invoiceId → invoice.id.
  // Both kinds drawn parent → child (left → right, lane order).
  const edges = [];
  const nodeIds = new Set(nodes.map(n => n.id));
  for (const n of nodes) {
    if (n.parentId && nodeIds.has(n.parentId)) {
      // Return docs (PR/NPR/SR/NSR) flow visually backward from invoice; mark
      // the edge so the canvas can render it as a return arrow.
      const isReturn = ['PR', 'NPR', 'SR', 'NSR'].includes(n.tranTypeCode);
      edges.push({ from: n.parentId, to: n.id, kind: isReturn ? 'return' : 'structural' });
    }
    // Payment → invoice (we draw invoice → payment, so flip direction in the
    // edge: from = invoice, to = payment).
    if (['CP', 'BP', 'CR', 'BR'].includes(n.tranTypeCode)) {
      for (const alloc of (n.invoiceAllocations || [])) {
        if (alloc?.invoiceId && nodeIds.has(alloc.invoiceId)) {
          edges.push({
            from: alloc.invoiceId,
            to: n.id,
            kind: 'payment',
            amount: Number(alloc.allocatedAmount) || 0
          });
        }
      }
    }
  }

  // 5. Compute chains — connected components rooted at nodes with no parent
  //    inside our visible set (and no incoming payment edge — payments are
  //    sinks, not roots).
  const incoming = new Map(); // nodeId → has-incoming?
  edges.forEach(e => incoming.set(e.to, true));
  const roots = nodes.filter(n => !incoming.get(n.id));

  // BFS from each root collecting reachable node ids.
  const adj = new Map();
  edges.forEach(e => {
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from).push(e.to);
  });
  const chains = roots.map(root => {
    const visited = new Set([root.id]);
    const stack = [root.id];
    while (stack.length) {
      const cur = stack.pop();
      (adj.get(cur) || []).forEach(next => {
        if (!visited.has(next)) { visited.add(next); stack.push(next); }
      });
    }
    return { rootId: root.id, nodeIds: [...visited] };
  });

  // 6. Aggregates + stuck list.
  const stats = computeStats(nodes, lanes);
  const terminalLaneId = lanes[lanes.length - 1].id;
  const stuck = computeStuck(chains, nodes, terminalLaneId, staleAfterDays);

  return { nodes, edges, chains, stats, stuck, lanes };
};

/**
 * Filter an already-built graph in memory. Avoids re-querying Firestore when
 * the user just changes the doc# / party / item / status filter.
 */
export const filterGraph = (graph, filters) => {
  const { docNumber, partyId, itemId, status } = filters || {};
  if (!docNumber && !partyId && !itemId && !status) return graph;

  const matchChain = (chain) => {
    const chainNodes = chain.nodeIds
      .map(id => graph.nodes.find(n => n.id === id))
      .filter(Boolean);

    if (docNumber) {
      const q = String(docNumber).trim().toLowerCase();
      if (!chainNodes.some(n => (n.docNumber || '').toLowerCase().includes(q))) return false;
    }
    if (partyId) {
      if (!chainNodes.some(n => n.partyId === partyId)) return false;
    }
    if (status) {
      if (!chainNodes.some(n => n.status === status)) return false;
    }
    if (itemId) {
      if (!chainNodes.some(n => (n.lines || []).some(l => l.itemId === itemId))) return false;
    }
    return true;
  };

  const filteredChains = graph.chains.filter(matchChain);
  const visibleIds = new Set(filteredChains.flatMap(c => c.nodeIds));
  const nodes = graph.nodes.filter(n => visibleIds.has(n.id));
  const edges = graph.edges.filter(e => visibleIds.has(e.from) && visibleIds.has(e.to));
  const stats = computeStats(nodes, graph.lanes);
  return { ...graph, nodes, edges, chains: filteredChains, stats };
};
